// frontend/src/pages/Tareas.js
import React, { useState, useEffect, useLayoutEffect, useRef, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import TareasSidebar from '../components/TareasSidebar';
import TaskCreationBar from '../components/TaskCreationBar';
import TaskRecipeView from '../components/TaskRecipeView';
import ReadableText from '../components/ReadableText';
import RateLimitWarning from '../components/RateLimitWarning';
import MentalLoadScreen from '../components/MentalLoadScreen';
import api, { fetchJobStatus } from '../api';
import './Tareas.css';
import ProfileBackground from '../components/ProfileBackground';
import { useAppBackground } from '../context/AppBackgroundContext';
import { useNotification } from '../context/NotificationContext';
import { useProgression } from '../context/ProgressionContext';
import { useI18n } from '../i18n';
import GuardianVignette from '../components/guardians/GuardianVignette';

const Tareas = () => {
  const params = useParams();
  const { progressionState, refreshState } = useProgression() || {};
  const navigate = useNavigate();
  const { addNotification, addConfirm } = useNotification();
  const { t } = useI18n();
  const { refreshUser } = useContext(AuthContext);
  const { displayedImage, settings } = useAppBackground() || {};

  const [currentTask, setCurrentTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitReset, setRateLimitReset] = useState(null);
  const [refreshHistory, setRefreshHistory] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [refinementInput, setRefinementInput] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isGeneratingAllImages, setIsGeneratingAllImages] = useState(false);
  const [isGeneratingAllSubsteps, setIsGeneratingAllSubsteps] = useState(false);
  const [backgroundJobs, setBackgroundJobs] = useState({}); // { [stepIndex]: { id, status } }
  const [showTutorialAdvance, setShowTutorialAdvance] = useState(false);

  // UI States
  const [taskPrompt, setTaskPrompt] = useState('');
  const mainContentRef = useRef(null);
  const taskTitleRef = useRef(null);

  // Responsive sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(false);

  // Patrón de sellado para useEffect en Tareas.js
  const hasFetched = useRef(false);
  const lastId = useRef(null);
  const skipNextTitleScrollRef = useRef(true);

  const scrollToTitle = () => {
    setTimeout(() => {
        taskTitleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const resetMainScroll = () => {
    const mainContent = mainContentRef.current;
    if (mainContent) {
      mainContent.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  useLayoutEffect(() => {
    skipNextTitleScrollRef.current = true;
    resetMainScroll();

    const frameId = window.requestAnimationFrame(resetMainScroll);
    return () => window.cancelAnimationFrame(frameId);
  }, [params.id]);

  const setTaskState = useCallback((taskData, { scrollToTaskTitle = true } = {}) => {
      setCurrentTask(taskData);
      setProgressText(taskData.progress || '');
      setError('');
      if (scrollToTaskTitle && !skipNextTitleScrollRef.current) {
        scrollToTitle();
      }
      skipNextTitleScrollRef.current = false;
  }, []);

  useEffect(() => {
    // Si ya se cargó esta ID durante el ciclo de vida del componente, o si estamos regenerando, abortar.
    if ((hasFetched.current && lastId.current === params.id) || isRegenerating) return;

    let isMounted = true;

    const loadTask = async () => {
        hasFetched.current = true; // Sellar inmediatamente
        lastId.current = params.id;

        setLoading(true);
        try {
            if (params.id) {
                const res = await api.get(`/tareas/${params.id}`);
                if (isMounted && res.data.task) {
                    setTaskState(res.data.task, { scrollToTaskTitle: false });
                }
            } else {
                if (isMounted) setCurrentTask(null);
            }
        } catch (err) {
            console.error("Error cargando tarea:", err);
            if (isMounted) setError(t('tasks.loadError'));
        } finally {
            if (isMounted) setLoading(false);
        }
    };
    loadTask();

    return () => { isMounted = false; };
  }, [params.id, isRegenerating, setTaskState, t]);

  const handleTaskGenerated = (generatedData) => {
      window.dispatchEvent(new CustomEvent('q-stamina-pulse'));
      setRateLimitReset(null);
      if (generatedData.task) {
          setTaskState(generatedData.task);
          setRefreshHistory(prev => prev + 1);
          if (generatedData.backgroundJobs) {
              setBackgroundJobs(prev => ({ ...prev, ...generatedData.backgroundJobs }));
          }
          if (progressionState?.stage === 'TAREAS') {
            setShowTutorialAdvance(true);
          } else if (refreshState) {
            refreshState();
          }
          navigate(`/tareas/${generatedData.task.id}`);
      }
  };

  const handleFinish = async () => {
      if (!currentTask?.id) return;
      addConfirm(t('tasks.completeConfirm'), async () => {
        try {
            await api.post('/tareas', {
                ...currentTask,
                accepted: true,
                completed: true,
                progress: progressText
            });
            setRefreshHistory(prev => prev + 1);
            addNotification(t('tasks.completedMessage'), "success");
            setCurrentTask(null);
            if (progressionState?.stage === 'TAREAS') {
              setShowTutorialAdvance(true);
            } else {
              if (refreshState) refreshState();
              navigate('/tareas');
            }
        } catch (err) {
            console.error(err);
            setError(t('tasks.finishError'));
        }
      });
  };

  const handleReject = async () => {
      if (!currentTask?.id) {
          setCurrentTask(null);
          return;
      }
      addConfirm(t('tasks.deleteHistoryConfirm'), async () => {
          try {
              await api.delete(`/tareas/${currentTask.id}`);
              setRefreshHistory(prev => prev + 1);
              setCurrentTask(null);
              navigate('/tareas');
          } catch (err) {
              addNotification(t('tasks.deleteError'), "error");
          }
      });
  };

  const handleToggleSubstep = async (stepIndex, substepIndex) => {
      if (!currentTask?.id) return;

      const newGuide = [...(currentTask.guide || [])];

      let step = newGuide[stepIndex];
      if (typeof step === 'string') {
          step = { text: step, completed: false, substeps: [] };
          newGuide[stepIndex] = step;
      }

      if (substepIndex === -1) {
          step.completed = !step.completed;
      } else {
          if (!step.substeps) step.substeps = [];
          const target = step.substeps[substepIndex];
          if (typeof target === 'string') {
              step.substeps[substepIndex] = { text: target, completed: true };
          } else if (target && typeof target === 'object') {
              step.substeps[substepIndex].completed = !step.substeps[substepIndex].completed;
          }
      }

      const updatedTask = { ...currentTask, guide: newGuide };
      setCurrentTask(updatedTask);

      try {
          await api.post('/tareas', {
              id: currentTask.id,
              mainTask: currentTask.main_task,
              accepted: true,
              guide: newGuide,
              progress: progressText
          });
          if (refreshState) refreshState();
      } catch (e) { console.error("Error guardando progreso checkbox", e); }
  };

  const handleImageGenerated = useCallback((stepIndex, updatedStep) => {
      if (refreshUser) refreshUser();
      setCurrentTask(prev => {
          if (!prev) return prev;
          const newGuide = [...(prev.guide || [])];
          newGuide[stepIndex] = updatedStep;
          return { ...prev, guide: newGuide };
      });
  }, [refreshUser]);

  useEffect(() => {
    const activeIndices = Object.keys(backgroundJobs).filter(idx => backgroundJobs[idx].status === 'pending');
    if (activeIndices.length === 0) return undefined;

    const intervalId = window.setInterval(async () => {
      for (const idx of activeIndices) {
        const jobInfo = backgroundJobs[idx];
        if (!jobInfo?.id) continue;

        try {
          const res = await fetchJobStatus(jobInfo.id);
          const job = res.data;
          if (job.status === 'completed') {
            if (job.result?.ok && job.result?.step) {
              const mainStepIndex = parseInt(String(idx).split('_')[0], 10);
              handleImageGenerated(mainStepIndex, job.result.step);
            }
            setBackgroundJobs(prev => {
              const next = { ...prev };
              delete next[idx];
              return next;
            });
          } else if (job.status === 'error') {
            addNotification(t('mutantBox.delayedError'), 'error');
            setBackgroundJobs(prev => {
              const next = { ...prev };
              delete next[idx];
              return next;
            });
          }
        } catch (e) {
          console.error(`Error polling task job for step ${idx}:`, e);
        }
      }
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [backgroundJobs, t, addNotification, handleImageGenerated]);

  const handleGenerateSubsteps = async (stepIndex) => {
      if (!currentTask?.id) return;
      try {
          const res = await api.post(`/tareas/${currentTask.id}/steps/${stepIndex}/substeps`);
          if (res.data.step) {
              setCurrentTask(prev => {
                  const newGuide = [...prev.guide];
                  newGuide[stepIndex] = res.data.step;
                  return { ...prev, guide: newGuide };
              });
          }
      } catch (err) {
          addNotification(t('tasks.substepsError', { message: err.response?.data?.error || err.message }), "error");
      }
  };

  const handleGenerateAllImages = async () => {
      if (!currentTask?.id || isGeneratingAllImages) return;

      const steps = currentTask.guide || [];
      const indicesToGenerate = steps.map((s, idx) => ({ s, idx })).filter(({ s, idx }) => !s.imageUrl && !backgroundJobs[idx]);

      if (indicesToGenerate.length === 0) {
          addNotification(t('tasks.allImagesDone'), "info");
          return;
      }

      addConfirm(t('tasks.generateImagesConfirm', { count: indicesToGenerate.length }), async () => {
        setIsGeneratingAllImages(true);

        for (let i = 0; i < indicesToGenerate.length; i++) {
            const { idx } = indicesToGenerate[i];
            try {
               const res = await api.post(`/tareas/${currentTask.id}/steps/${idx}/image`);
               if (res.status === 202 && res.data?.jobId) {
                   setBackgroundJobs(prev => ({
                       ...prev,
                       [idx]: { id: res.data.jobId, status: 'pending' }
                   }));
               } else if (res.data.step) {
                   handleImageGenerated(idx, res.data.step);
               }
            } catch (e) {
               console.error(`Error generando imagen paso ${idx}`, e);
               const msg = e.response?.data?.error || e.message || 'Error';
               if (e.response?.status === 429) {
                   addNotification('Límite de peticiones alcanzado. Pausando...', 'warning');
                   break;
               }
               addNotification(`${t('tasks.visualStepAlt', { index: idx + 1 })}: ${msg}`, 'error');
            }

            // "Cool down" delay between requests to avoid rate limits
            if (i < indicesToGenerate.length - 1) {
                await new Promise(r => setTimeout(r, 4500));
            }
        }

        setIsGeneratingAllImages(false);
      });
  };

  const handleGenerateAllSubsteps = async () => {
    if (!currentTask?.id || isGeneratingAllSubsteps) return;

    addConfirm(t('tasks.generateAllSubstepsConfirm'), async () => {
      setIsGeneratingAllSubsteps(true);
      try {
          const res = await api.post(`/tareas/${currentTask.id}/substeps/all`);
          if (res.data.guide) {
               setCurrentTask(prev => ({
                   ...prev,
                   guide: res.data.guide
               }));
          }
      } catch (e) {
          console.error("Error generando todos los sub-pasos", e);
          addNotification(t('tasks.bulkSubstepsError'), "error");
      } finally {
          setIsGeneratingAllSubsteps(false);
      }
    });
  };

  const handleRefineTask = async () => {
      if (!currentTask?.id || !refinementInput.trim() || isRegenerating) return;

      window.dispatchEvent(new CustomEvent('q-stamina-pulse'));
      setIsRegenerating(true);
      setError('');
      try {
          const res = await api.post(`/tareas/${currentTask.id}/refine`, {
              refinementPrompt: refinementInput
          });
          if (res.data.task) {
              setTaskState(res.data.task);
              if (res.data.backgroundJobs) {
                  setBackgroundJobs(prev => ({ ...prev, ...res.data.backgroundJobs }));
              }
              setRefinementInput('');
              addNotification(t('tasks.refinedSuccess'), "success");
          }
      } catch (err) {
          console.error("Error refinando tarea:", err);
          setError(err.response?.data?.error || t('tasks.refineError'));
      } finally {
          setIsRegenerating(false);
      }
  };

  const handleRefinementKeyDown = (e) => {
    if (e.altKey && e.key === 'Enter') {
      e.preventDefault(); // Evita el salto de línea al enviar
      if (!isRegenerating && refinementInput.trim()) {
        handleRefineTask();
      }
    }
  };

  return (
    <>
      {showTutorialAdvance && (
        <GuardianVignette
          buttonText={t('guardian.stages.continueBtn')}
          message={t('guardian.stages.tareasAdvanceMsg')}
          onContinue={() => {
            api.post('/progression/advance', { nextStage: 'CONCIENCIA' }).then(() => refreshState?.());
            setShowTutorialAdvance(false);
          }}
        />
      )}
      {settings?.enabled && !currentTask && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}
      <div
        className={`tareas-page ${settings?.enabled && !currentTask && displayedImage ? "with-app-background" : ""} ${desktopSidebarOpen ? "desktop-sidebar-is-open" : "desktop-sidebar-is-closed"}`.trim()}
        style={{ position: "relative", zIndex: 2 }}
      >
      {!sidebarOpen && (
        <button
          type="button"
          className="mobile-sidebar-toggle sidebar-folder-toggle sidebar-folder-toggle--floating"
          onClick={() => setSidebarOpen(true)}
          aria-controls="tasks-history-sidebar"
          aria-expanded={false}
          aria-label={t('tasks.history')}
          title={t('tasks.history')}
        >
          📁
        </button>
      )}

      {!desktopSidebarOpen && (
        <button
          type="button"
          className="desktop-sidebar-toggle-btn sidebar-folder-toggle sidebar-folder-toggle--floating"
          onClick={() => setDesktopSidebarOpen(true)}
          aria-controls="tasks-history-sidebar"
          aria-expanded={false}
          aria-label={t('tasks.showHistory')}
          title={t('tasks.showHistory')}
        >
          📁
        </button>
      )}

      <div
        id="tasks-history-sidebar"
        className={`tareas-sidebar-container ${sidebarOpen ? 'mobile-open' : ''} ${!desktopSidebarOpen ? 'desktop-closed' : ''}`}
      >
        {(sidebarOpen || desktopSidebarOpen) && (
          <div className="tareas-sidebar-toggle-row">
            <button
              type="button"
              className="sidebar-folder-toggle sidebar-folder-toggle--inside"
              onClick={() => {
                setSidebarOpen(false);
                setDesktopSidebarOpen(false);
              }}
              aria-controls="tasks-history-sidebar"
              aria-expanded={true}
              aria-label={sidebarOpen ? t('tasks.closeHistory') : t('tasks.hideHistory')}
              title={sidebarOpen ? t('tasks.closeHistory') : t('tasks.hideHistory')}
            >
              📂
            </button>
          </div>
        )}
        <TareasSidebar
            onNuevaTarea={() => {
                setCurrentTask(null);
                navigate('/tareas');
                setSidebarOpen(false);
            }}
            onSelectTask={(id) => {
                setSidebarOpen(false);
            }}
            selectedTaskId={currentTask?.id}
            refreshFlag={refreshHistory}
            onTaskMoved={(task) => {
                if (task && String(task.id) === String(currentTask?.id)) {
                    setTaskState(task);
                }
                setRefreshHistory(prev => prev + 1);
            }}
        />
      </div>

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

      <div className="tareas-main" ref={mainContentRef}>
        {progressionState?.stage === 'TAREAS' && (
          <div className="cyber-hud-counter" style={{ color: 'var(--cyber-cyan)', letterSpacing: '2px', padding: '10px 0 20px', fontFamily: 'Orbitron, sans-serif', textShadow: '0 0 10px var(--cyber-cyan)', fontSize: '1.2rem', textAlign: 'center', width: '100%', flexShrink: 0 }}>
            ANCLANDO QUIDDIDAD FÍSICA...<br/>
            Tareas Aceptadas: {progressionState.stage_interactions || 0}/1
          </div>
        )}
        <div className="task-top-bar">
             <div className="task-bar-content">
                <TaskCreationBar id="tasks-list"
                    prompt={taskPrompt}
                    setPrompt={setTaskPrompt}
                    onGenerated={handleTaskGenerated}
                    onRateLimited={(resetTime) => setRateLimitReset(resetTime)}
                />
             </div>
        </div>

        {error && <div className="error-banner">{error}</div>}
        {rateLimitReset && (
            <RateLimitWarning
                resetTime={rateLimitReset}
                onReset={() => setRateLimitReset(null)}
            />
        )}
        {loading && <MentalLoadScreen loading={loading} message={t('tasks.localProcessing')} />}

        {!currentTask && !loading && !rateLimitReset && (
            <div className="empty-state">
                <h2>{t('tasks.consciousManager')}</h2>
                <p>{t('tasks.emptyDescription')}</p>
            </div>
        )}

        {currentTask && (
            <div className="active-task-container">
                {/* Árbol de Original Request y Refinamientos */}
                <div className="task-refinement-tree">
                    <h4 className="tree-root-title">{t('tasks.originalRequest')}</h4>
                    {(currentTask.meta?.refinementHistory || [{ role: 'user', content: currentTask.original_prompt || currentTask.main_task }]).map((node, index) => (
                        <div key={index} className={`tree-node level-${index}`}>
                            {index === 0 ? (
                                <p className="original-prompt"><ReadableText text={node.content} label={t('tasks.readOriginalRequest')}>{node.content}</ReadableText></p>
                            ) : (
                                <div className="refinement-branch">
                                    <span className="branch-icon">↳</span>
                                    <p className="refinement-prompt"><strong>{t('tasks.refinement', { index })}</strong> <ReadableText text={node.content} label={t('tasks.readRefinement')}>{node.content}</ReadableText></p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="task-refine-input-container" style={{ marginTop: '15px', marginBottom: '30px' }}>
                  <textarea
                    value={refinementInput}
                    onChange={(e) => setRefinementInput(e.target.value)}
                    onKeyDown={handleRefinementKeyDown}
                    placeholder={t('tasks.refinePlaceholder')}
                    rows={3}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <small style={{ color: 'var(--text-muted, #888)', fontSize: '0.85rem' }}>
                      {t('tasks.refineHint')}
                    </small>
                    <button
                      className={`btn-refine ${isRegenerating ? 'btn-processing' : ''}`}
                      onClick={handleRefineTask}
                      disabled={isRegenerating || !refinementInput.trim()}
                    >
                      {isRegenerating ? t('tasks.mutatingTask') : t('tasks.refineTask')}
                    </button>
                  </div>
                </div>

                <div className="task-header-section" ref={taskTitleRef}>
                    <h2 className="task-main-title"><ReadableText text={currentTask.main_task} label={t('tasks.readGeneratedTask')}>{currentTask.main_task}</ReadableText></h2>
                    <div className="badges">
                        {currentTask.completed && <span className="badge completed">{t('tasks.completedBadge')}</span>}
                    </div>
                </div>

                {currentTask.guide && currentTask.guide.length > 0 && (
                     <div className="global-actions" style={{ marginBottom: 10, display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button
                            id="btn-task-visualize"
                            className={`btn-secondary ${isGeneratingAllImages ? 'btn-processing' : ''}`}
                            onClick={handleGenerateAllImages}
                            disabled={isGeneratingAllImages || isGeneratingAllSubsteps}
                            title={isGeneratingAllSubsteps ? t('tasks.waitDetails') : t('tasks.generateImagesAll')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem' }}
                        >
                            {isGeneratingAllImages ? `🎨 ${t('tasks.generatingVisuals')}` : `🎨 ${t('tasks.generateVisualsAll')}`}
                        </button>

                        <button
                            id="btn-task-substeps"
                            className={`btn-secondary ${isGeneratingAllSubsteps ? 'btn-processing' : ''}`}
                            onClick={handleGenerateAllSubsteps}
                            disabled={isGeneratingAllSubsteps || isGeneratingAllImages}
                            title={isGeneratingAllImages ? t('tasks.waitVisuals') : t('tasks.generateSubstepsAllTitle')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem' }}
                        >
                            {isGeneratingAllSubsteps ? `📝 ${t('tasks.generatingDetails')}` : `📝 ${t('tasks.generateSubstepsAll')}`}
                        </button>
                     </div>
                )}

                <TaskRecipeView
                    recipe={{
                        title: currentTask.main_task,
                        summary: currentTask.generated_recipe?.summary || currentTask.summary,
                        guide: currentTask.guide,
                        raw: currentTask.generated_recipe?.raw || currentTask.raw
                    }}
                    onToggleSubstep={handleToggleSubstep}
                    isDraft={false}
                    taskId={currentTask.id}
                    onImageGenerated={handleImageGenerated}
                    onGenerateSubsteps={handleGenerateSubsteps}
                    backgroundJobs={backgroundJobs}
                    onBackgroundJobStarted={(idx, jobId) => setBackgroundJobs(prev => ({ ...prev, [idx]: { id: jobId, status: 'pending' } }))}
                />

                {/* Botones de Acción Global (Borrador Eliminado) */}
                <div className="action-buttons-row">
                  {!currentTask.completed && (
                    <button className="btn-success" onClick={handleFinish}>{t('tasks.finishTask')}</button>
                  )}
                  <button className="btn-danger btn-delete-task-active" onClick={handleReject}>{t('tasks.discard')}</button>
                </div>
            </div>
        )}

      </div>
      </div>
    </>
  );
};

export default Tareas;
